import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, UserPlus, Upload, Mail, CheckCircle2, X, Send } from 'lucide-react';
import { format } from 'date-fns';
import { listPeople, listPendingInvitations, revokeInvitation, unassignBand, resendInvitation } from '@/lib/admin/people-api';
import { AddPersonDialog } from '@/components/admin/people/AddPersonDialog';
import { BulkUploadDialog } from '@/components/admin/people/BulkUploadDialog';
import { useToast } from '@/hooks/use-toast';

export default function People() {
  const { orgId } = useOutletContext<{ orgId: string }>();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const peopleQ = useQuery({ queryKey: ['admin', 'people', orgId], queryFn: () => listPeople(orgId) });
  const pendingQ = useQuery({ queryKey: ['admin', 'pending', orgId], queryFn: () => listPendingInvitations(orgId) });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeInvitation(id),
    onSuccess: () => {
      toast({ title: 'Invitation revoked' });
      qc.invalidateQueries({ queryKey: ['admin', 'pending', orgId] });
    },
    onError: (e: any) => toast({ title: 'Failed', description: e?.message ?? '', variant: 'destructive' }),
  });

  const resend = useMutation({
    mutationFn: (id: string) => resendInvitation(id),
    onSuccess: (res) => {
      if (res?.ok) toast({ title: 'Invite re-sent', description: 'Email queued for delivery.' });
      else toast({ title: 'Could not resend', description: res?.error ?? 'unknown', variant: 'destructive' });
    },
    onError: (e: any) => toast({ title: 'Failed', description: e?.message ?? '', variant: 'destructive' }),
  });

  const removeBand = useMutation({
    mutationFn: ({ employeeId, bandId }: { employeeId: string; bandId: string }) =>
      unassignBand(orgId, employeeId, bandId),
    onSuccess: () => {
      toast({ title: 'Band removed' });
      qc.invalidateQueries({ queryKey: ['admin', 'people', orgId] });
    },
    onError: (e: any) => toast({ title: 'Failed', description: e?.message ?? '', variant: 'destructive' }),
  });

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-heading font-semibold">People</h2>
          <p className="text-sm text-muted-foreground">
            Manage members and pending invitations. Identities are isolated — only org-salted hashes are shown for members.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAddOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" /> Add one
          </Button>
          <Button onClick={() => setBulkOpen(true)}>
            <Upload className="h-4 w-4 mr-2" /> Bulk upload
          </Button>
        </div>
      </div>

      <Alert className="border-success/40 bg-success/5">
        <CheckCircle2 className="h-4 w-4 text-success" />
        <AlertDescription>
          Email delivery is wired — invitations send automatically and you can resend any pending one from the Pending tab.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">
            Members {peopleQ.data && <span className="ml-1 text-muted-foreground">({peopleQ.data.length})</span>}
          </TabsTrigger>
          <TabsTrigger value="pending">
            Pending {pendingQ.data && <span className="ml-1 text-muted-foreground">({pendingQ.data.length})</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Members</CardTitle>
              <CardDescription>One row per person who has signed in or been auto-provisioned.</CardDescription>
            </CardHeader>
            <CardContent>
              {peopleQ.isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : peopleQ.error ? (
                <p className="text-sm text-destructive">{(peopleQ.error as Error).message}</p>
              ) : !peopleQ.data?.length ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No members yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b border-border">
                        <th className="p-2">Identity</th>
                        <th className="p-2">Status</th>
                        <th className="p-2">Joined</th>
                        <th className="p-2">Bands</th>
                      </tr>
                    </thead>
                    <tbody>
                      {peopleQ.data.map((p) => (
                        <tr key={p.employee_id} className="border-b border-border/50 align-top">
                          <td className="p-2 font-mono text-xs">#{p.hash_prefix}</td>
                          <td className="p-2">
                            {p.is_demo ? (
                              <Badge variant="secondary">demo</Badge>
                            ) : p.signed_in ? (
                              <Badge variant="default">active</Badge>
                            ) : (
                              <Badge variant="outline">awaiting sign-in</Badge>
                            )}
                          </td>
                          <td className="p-2 text-muted-foreground text-xs">
                            {format(new Date(p.created_at), 'MMM d, yyyy')}
                          </td>
                          <td className="p-2">
                            {p.bands.length === 0 ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {p.bands.map((b) => (
                                  <span
                                    key={`${p.employee_id}-${b.band_id}`}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-xs"
                                  >
                                    <span className="text-muted-foreground capitalize">{b.dimension.replace('_', ' ')}:</span>
                                    <span>{b.label}</span>
                                    {b.band_id && (
                                      <button
                                        onClick={() => removeBand.mutate({ employeeId: p.employee_id, bandId: b.band_id! })}
                                        className="ml-0.5 hover:text-destructive"
                                        title="Unassign"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pending invitations</CardTitle>
              <CardDescription>Auto-applied at first sign-in. Email shown because the admin entered it.</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingQ.isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : pendingQ.error ? (
                <p className="text-sm text-destructive">{(pendingQ.error as Error).message}</p>
              ) : !pendingQ.data?.length ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No pending invitations.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b border-border">
                        <th className="p-2">Email</th>
                        <th className="p-2">Staged</th>
                        <th className="p-2">Expires</th>
                        <th className="p-2">Bands</th>
                        <th className="p-2 w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingQ.data.map((p) => (
                        <tr key={p.id} className="border-b border-border/50 align-top">
                          <td className="p-2 font-mono text-xs">
                            <Mail className="h-3 w-3 inline mr-1 text-muted-foreground" />
                            {p.email}
                          </td>
                          <td className="p-2 text-muted-foreground text-xs">
                            {format(new Date(p.created_at), 'MMM d')}
                          </td>
                          <td className="p-2 text-muted-foreground text-xs">
                            {format(new Date(p.expires_at), 'MMM d, yyyy')}
                          </td>
                          <td className="p-2">
                            {p.bands.length === 0 ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {p.bands.map((b, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-xs">
                                    <span className="text-muted-foreground capitalize">{b.dimension.replace('_', ' ')}:</span>
                                    <span>{b.label}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="p-2 text-right whitespace-nowrap">
                            <Button size="sm" variant="ghost" disabled={resend.isPending} onClick={() => resend.mutate(p.id)}>
                              <Send className="h-3 w-3 mr-1" /> Resend
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => revoke.mutate(p.id)}>
                              Revoke
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AddPersonDialog orgId={orgId} open={addOpen} onOpenChange={setAddOpen} />
      <BulkUploadDialog orgId={orgId} open={bulkOpen} onOpenChange={setBulkOpen} />
    </div>
  );
}
